import * as endpoints from "../../lib/api/endpoints";
import * as routes from "../../constants/routes";
import * as validationLib from "../../lib/validation";
import callApplicationLead from "../../lib/callApplicationLead";
import Card from "../Card";
import ErrorBoundary from "../ErrorBoundary";
import getFirstInvalidKey, {ValidationType} from "../../lib/getFirstInvalidKey";
import hasFailingValidation from "../../lib/hasFailingValidation";
import isReviewKey from "../../lib/isReviewKey";
import React, {useEffect, useRef, useState} from "react";
import ReviewDescription from "../ReviewDescription";
import ReviewInput from "../ReviewInput";
import ReviewLabel from "../ReviewLabel";
import ReviewLink from "../ReviewLink";
import SetNoIndex from "../../lib/SetNoIndex";
import StarRating from "../StarRating";
import styles from "./ReviewScreen.module.css";
import trustpilotLogo from "../../images/trustpilotLogo.svg";
import useFocusInvalid from "../../lib/useFocusInvalid";
import usePageView from "../../lib/usePageView";
import validateApplicationField from "../../lib/validateApplicationField";
import ValidationList from "../ValidationList";
import {InputType} from "../LinedInput";
import {Review, ReviewKeys, ReviewValidation} from "../../lib/api/models";
import {useParams, RouteComponentProps} from "react-router-dom";

const strings = {
    confirmReview: "I confirm this review is about my own genuine experience. ",
    confirmReviewEligable: "eligible to leave this review",
    confirmReviewIAm: "I am",
    confirmReviewLine2:
        ", and have not been offered any incentive or payment to leave a review for this company.",
    contactOneFB:
        "Let 1st Financial Bank know about your review. Just check the box and we'll notify them.",
    email: "Email",
    emailPlaceholder: "your@email.com",
    header: "1st Financial Bank USA",
    howToReview: "How to write a useful review",
    inputMessage: (charsLeft: number) =>
        `Type ${charsLeft} more ${charsLeft === 1 ? "character" : "characters"}.`,
    reviewLabelText: "Tell us about your experience",
    reviewLinkText: "Read our Guidelines for Reviewers",
    reviewTitle: "Give your review a title",
    starTitle: "Rate your recent experience",
    submit: "Submit",
    submitting: "Submitting...",
    subtitle: "1fbusa.com",
    title: "1st Financial Bank USA Review",
};

const DEFAULT_TITLE_MIN_CHARS = 3;
const DEFAULT_MINIMUM_CHARS = 10;
const CONTACT_CHECKBOX_ID = "contactCheckbox";
const CONFIRM_CHECKBOX_ID = "confirmCheckbox";
const EMPTY_REVIEW_VALIDATION: ReviewValidation = {};
const SPECIAL_CHARS_REGEX = "^[.?!:;\\n]+$";

function getTitleFromDescription(d: string): string {
    for (let i = 0; i < d.length - 1; i++) {
        if (
            (d.charAt(i).match(SPECIAL_CHARS_REGEX) && d.charAt(i + 1) === " ") ||
            d.charAt(i) === "\n"
        ) {
            return d.substring(0, i);
        }
    }
    return d;
}

type URLParams = {
    applicationId: string;
};

export default function ReviewScreen(props: RouteComponentProps<undefined>) {
    useEffect(() => {
        document.title = strings.title;
    }, []);

    SetNoIndex();
    usePageView(strings.title);

    const windowUrl = window.location.href;

    const urlParams: URLParams = useParams();

    const EMPTY_REVIEW: Review = {
        [ReviewKeys.Text]: "",
        [ReviewKeys.Title]: "",
        [ReviewKeys.Email]: "",
        [ReviewKeys.InvitationCode]: urlParams.applicationId,
        [ReviewKeys.Rating]: 0,
    };

    const [loading, setLoading] = useState(false);
    const [review, setReview] = useState(EMPTY_REVIEW);
    const [hasTyped, setHasTyped] = useState(false);
    const [hasTypedTitle, setHasTypedTitle] = useState(false);
    const [validation, setValidation] = useState<validationLib.Validation>({});
    const [validationCount, setValidationCount] = useState(1);
    const reviewValidation = validation.errors ?? EMPTY_REVIEW_VALIDATION;

    const textHasChanged = useRef(false);
    const titleHasChanged = useRef(false);

    // Text Description
    const titleCharsLeft = DEFAULT_TITLE_MIN_CHARS - review[ReviewKeys.Title].length;
    const titleMessageToDisplay =
        hasTyped && titleCharsLeft > 0 ? strings.inputMessage(titleCharsLeft) : "";

    // Title
    const charsLeft = DEFAULT_MINIMUM_CHARS - review[ReviewKeys.Text].length;
    const messageToDisplay = hasTyped && charsLeft > 0 ? strings.inputMessage(charsLeft) : "";

    // Checkbox
    const [contactOneFB, setContactOneFB] = useState(false);
    const [confirmReview, setConfirmReview] = useState(false);

    const isMountedRef = useRef<boolean>(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    function onBlurInput(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;
        if (isReviewKey(name)) {
            const {isValid, messages} = validateApplicationField(name, value);
            if (isValid) {
                // Need application lead for title as well
                if (name === ReviewKeys.Text && !hasTypedTitle && textHasChanged.current) {
                    callApplicationLead(
                        ReviewKeys.Text,
                        review[ReviewKeys.InvitationCode],
                        windowUrl,
                        value.toString(),
                    );
                    callApplicationLead(
                        ReviewKeys.Title,
                        review[ReviewKeys.InvitationCode],
                        windowUrl,
                        getTitleFromDescription(value.toString()),
                    );
                    textHasChanged.current = false;
                    titleHasChanged.current = false;
                } else if (name === ReviewKeys.Text && textHasChanged.current) {
                    callApplicationLead(
                        ReviewKeys.Text,
                        review[ReviewKeys.InvitationCode],
                        windowUrl,
                        value,
                    );
                    textHasChanged.current = false;
                } else if (name === ReviewKeys.Title && titleHasChanged.current) {
                    callApplicationLead(
                        ReviewKeys.Title,
                        review[ReviewKeys.InvitationCode],
                        windowUrl,
                        value,
                    );
                    titleHasChanged.current = false;
                }
                return;
            }

            setValidation((prev) => ({
                ...prev,
                errors: {
                    ...prev?.errors,
                    [name]: messages,
                },
            }));
        }
    }

    function clearValidation(validationKey: ReviewKeys) {
        setValidation((prev) => {
            if (prev.generic || (prev.errors && prev.errors[validationKey])) {
                const nextValidation = {
                    ...prev,
                };
                delete nextValidation.generic;
                if (prev && prev.errors) {
                    delete prev.errors[validationKey];
                }
                return nextValidation;
            }
            return prev;
        });
    }

    function onChangeReviewField(name: ReviewKeys, value: string | number) {
        if (
            !hasTyped &&
            (name === ReviewKeys.Text || name === ReviewKeys.Title) &&
            value.toString().length > 0
        ) {
            setHasTyped(true);
        }
        // description no longer affects title once title box interacted w/
        if (!hasTypedTitle && name === ReviewKeys.Title && value.toString().length > 0) {
            setHasTypedTitle(true);
        }
        // title becomes description up to special character
        if (!hasTypedTitle && name === ReviewKeys.Text) {
            setReview((prevReview) => ({
                ...prevReview,
                [ReviewKeys.Text]: value.toString(),
                [ReviewKeys.Title]: getTitleFromDescription(value.toString()),
            }));
            textHasChanged.current = true;
            titleHasChanged.current = true;
        } else {
            setReview((prevReview) => ({
                ...prevReview,
                [name]: value,
            }));
            if (name === ReviewKeys.Text) {
                textHasChanged.current = true;
            } else if (name === ReviewKeys.Title) {
                titleHasChanged.current = true;
            }
        }
        // Simple change so call to app lead may be done here
        if (name === ReviewKeys.Rating) {
            callApplicationLead(
                name,
                review[ReviewKeys.InvitationCode],
                windowUrl,
                value.toString(),
            );
        }
        clearValidation(name);
    }

    function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (hasFailingValidation(validation)) {
            return;
        }

        callApplicationLead("review-submit", review[ReviewKeys.InvitationCode], windowUrl);

        setLoading(true);
        endpoints
            .review(review)
            .then(() => props.history.replace(routes.REVIEW_COMPLETE))
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                const errValidation = validationLib.getValidationObj(err);
                setValidation(errValidation);
            })
            .finally(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setLoading(false);
                setValidationCount((prev) => prev + 1);
            });
    }

    const textRef = useRef<HTMLTextAreaElement>(null);
    const titleRef = useRef<HTMLInputElement>(null);
    const emailRef = useRef<HTMLInputElement>(null);

    const firstInvalidKey = getFirstInvalidKey(validation, ValidationType.Review);
    let firstInvalidRef:
        | React.RefObject<HTMLInputElement>
        | React.RefObject<HTMLTextAreaElement>
        | undefined = undefined;
    if (firstInvalidKey === ReviewKeys.Text) {
        firstInvalidRef = textRef;
    } else if (firstInvalidKey === ReviewKeys.Title) {
        firstInvalidRef = titleRef;
    } else if (firstInvalidKey === ReviewKeys.Email) {
        firstInvalidRef = emailRef;
    }
    useFocusInvalid(validationCount, firstInvalidRef?.current);

    function renderEmailAndCheckboxes() {
        return (
            <>
                <div className={styles.checkboxContainer}>
                    <input
                        checked={contactOneFB}
                        className={styles.checkboxHover}
                        id={CONTACT_CHECKBOX_ID}
                        required={true}
                        type="checkbox"
                        onChange={(e) => {
                            callApplicationLead(
                                "review-checkbox-notify",
                                review[ReviewKeys.InvitationCode],
                                windowUrl,
                                e.target.checked.toString(),
                            );
                            setContactOneFB(e.target.checked);
                        }}
                    />
                    <div className={styles.checkboxText}>
                        <label htmlFor={CONTACT_CHECKBOX_ID} className={styles.checkboxHover}>
                            {strings.contactOneFB}
                        </label>
                    </div>
                </div>

                <div className={styles.lastCheckbox}>
                    <input
                        checked={confirmReview}
                        className={styles.checkboxHover}
                        id={CONFIRM_CHECKBOX_ID}
                        required={true}
                        type="checkbox"
                        onChange={(e) => {
                            callApplicationLead(
                                "review-checkbox-confirm",
                                review[ReviewKeys.InvitationCode],
                                windowUrl,
                                e.target.checked.toString(),
                            );
                            setConfirmReview(e.target.checked);
                        }}
                    />
                    <div className={styles.checkboxText}>
                        <label htmlFor={CONFIRM_CHECKBOX_ID} className={styles.checkboxHover}>
                            {strings.confirmReview}
                        </label>
                        <span>
                            {strings.confirmReviewIAm}{" "}
                            <a
                                href="https://support.trustpilot.com/hc/en-us/articles/205675248"
                                target="_blank"
                                rel="noreferrer"
                            >
                                {strings.confirmReviewEligable}
                            </a>
                            {strings.confirmReviewLine2}
                        </span>
                    </div>
                </div>

                <ReviewInput
                    className={styles.emailTitle}
                    id={ReviewKeys.Email}
                    name={ReviewKeys.Email}
                    onBlur={onBlurInput}
                    onChange={(e) => onChangeReviewField(ReviewKeys.Email, e.target.value)}
                    placeholder={strings.emailPlaceholder}
                    ref={emailRef}
                    renderSelectAll={false}
                    required={true}
                    title={strings.email}
                    type={InputType.Email}
                    validationMessage={reviewValidation[ReviewKeys.Email]}
                    value={review[ReviewKeys.Email]}
                />

                <button disabled={loading} className={styles.emailButton}>
                    {loading ? strings.submitting : strings.submit}
                </button>

                {validation.generic ? (
                    <ValidationList className={styles.validation} messages={validation.generic} />
                ) : null}
            </>
        );
    }

    function renderDescription() {
        return (
            <>
                <ReviewDescription
                    className={styles.reviewDescription}
                    id={ReviewKeys.Text}
                    name={ReviewKeys.Text}
                    onBlur={onBlurInput}
                    onChange={(e) => {
                        onChangeReviewField(ReviewKeys.Text, e.target.value);
                    }}
                    ref={textRef}
                    required={true}
                    reviewLabelText={strings.reviewLabelText}
                    reviewLinkText={strings.reviewLinkText}
                    reviewLinkHref="https://legal.trustpilot.com/for-reviewers/guidelines-for-reviewers"
                    value={review[ReviewKeys.Text]}
                    message={messageToDisplay}
                    validationMessage={reviewValidation[ReviewKeys.Text]}
                />
                <ReviewLink
                    className={styles.howToLink}
                    label={strings.howToReview}
                    href="https://support.trustpilot.com/hc/en-us/articles/223402108"
                />
                <ReviewInput
                    id={ReviewKeys.Title}
                    name={ReviewKeys.Title}
                    title={strings.reviewTitle}
                    value={review[ReviewKeys.Title]}
                    onBlur={onBlurInput}
                    onChange={(e) => {
                        onChangeReviewField(ReviewKeys.Title, e.target.value);
                    }}
                    selectAll={() => {
                        titleRef.current?.focus();
                        titleRef.current?.select();
                    }}
                    message={titleMessageToDisplay}
                    ref={titleRef}
                    required={true}
                    validationMessage={reviewValidation[ReviewKeys.Title]}
                />
                {hasTyped ? renderEmailAndCheckboxes() : null}
            </>
        );
    }

    function renderForm() {
        return (
            <form onSubmit={onSubmit}>
                <ReviewLabel label={strings.starTitle} className={styles.starTitle} />
                <StarRating
                    value={review[ReviewKeys.Rating]}
                    setValue={(n: number) => onChangeReviewField(ReviewKeys.Rating, n)}
                />
                {review[ReviewKeys.Rating] !== 0 ? renderDescription() : null}
            </form>
        );
    }

    return (
        <ErrorBoundary>
            <div className={styles.wrapper}>
                <div className={styles.firstContainer}>
                    <div className={styles.logoContainer}>
                        <img
                            className={styles.logo}
                            alt="TrustPilot Reviews"
                            src={trustpilotLogo}
                        />
                    </div>
                </div>
                <div className={styles.breadcrumb}>
                    <div className={styles.businessInfo}>
                        <img
                            alt=""
                            src={`${process.env.REACT_APP_BASE_PATH}/default_logo.svg`}
                            className={styles.businessLogo}
                        />
                        <div>
                            <div className={styles.header}>{strings.header}</div>
                            <div className={styles.subtitle}>{strings.subtitle}</div>
                        </div>
                    </div>
                </div>

                <div className={styles.body}>
                    <Card>{renderForm()}</Card>
                </div>
            </div>
        </ErrorBoundary>
    );
}
